import { Link, NavLink } from "react-router-dom";
import { Moon, Sun } from "lucide-react";
import { Button } from "./Button";
import { t } from "../i18n/t";

/**
 * The redesign's top bar (guide §3): blue `--color-topbar-bg` strip; inner box `max-width: --topbar-max` with
 * 10×48 padding, wrapping; white logo square with the blue "L" and the 15/600 wordmark; white nav links with
 * the active one on `--color-topbar-active` (`aria-current="page"`, added by `NavLink`); on the right the
 * round mono avatar, the operator name, a compact transparent theme toggle (`sun`/`moon`) and Sign out.
 *
 * Props-only: `RootLayout` (step 3) owns the session, features and theme hooks and hands the values down.
 */
export function TopBar({
  operator,
  practiceEnabled = false,
  securityLink = false,
  theme = "light",
  onToggleTheme,
  onSignOut,
}: {
  operator?: string | undefined;
  practiceEnabled?: boolean;
  /** xmg-sec-mfa: a signed-in USER's own two-step authentication page. Off for an operator-key session, which
   * has no user and no factor. */
  securityLink?: boolean;
  theme?: "light" | "dark";
  onToggleTheme?: () => void;
  onSignOut?: () => void;
}): JSX.Element {
  return (
    <header className="topbar">
      <div className="topbar__inner">
        <Link to="/" className="topbar__brand">
          {/* Theme-invariant by design: the white square and blue L are the same in both themes. */}
          <span className="topbar__logo" aria-hidden="true">
            L
          </span>
          <span className="topbar__wordmark">{t("common.app_name")}</span>
        </Link>
        <nav className="topbar__nav" aria-label={t("nav.primary")}>
          <NavLink to="/" end className={topbarLinkClass}>
            {t("nav.migrations")}
          </NavLink>
          <NavLink to="/connections" className={topbarLinkClass}>
            {t("nav.connections")}
          </NavLink>
          {/* P5-web §1: practice routes 404 when `LUCIMIG_PRACTICE_ENABLED` is off, so the nav link is gated on
              the same flag rather than always rendered and left to 404 on click. */}
          {practiceEnabled ? (
            <NavLink to="/practice" className={topbarLinkClass}>
              {t("nav.practice")}
            </NavLink>
          ) : null}
          <NavLink to="/settings" className={topbarLinkClass}>
            {t("nav.settings")}
          </NavLink>
          {securityLink ? (
            <NavLink to="/account/security" className={topbarLinkClass}>
              {t("nav.security")}
            </NavLink>
          ) : null}
        </nav>
        <div className="topbar__user">
          <span className="topbar__avatar" aria-hidden="true">
            {operatorInitials(operator)}
          </span>
          <span className="topbar__name">{operator ?? t("common.unknown")}</span>
          <Button
            size="compact"
            className="topbar__button topbar__toggle"
            onClick={onToggleTheme}
            aria-label={theme === "dark" ? t("nav.theme_to_light") : t("nav.theme_to_dark")}
          >
            {theme === "dark" ? (
              <Sun size={16} strokeWidth={2} aria-hidden="true" />
            ) : (
              <Moon size={16} strokeWidth={2} aria-hidden="true" />
            )}
          </Button>
          <Button size="compact" className="topbar__button" onClick={onSignOut}>
            {t("nav.sign_out")}
          </Button>
        </div>
      </div>
    </header>
  );
}

function topbarLinkClass({ isActive }: { isActive: boolean }): string {
  return isActive ? "topbar__link topbar__link--active" : "topbar__link";
}

function operatorInitials(operator: string | undefined): string {
  const source = operator ?? "?";
  return (
    source
      .replace(/[@.].*$/, "")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}
